import React, { useEffect, useState } from 'react';
import {
  Users,
  UserPlus,
  Upload,
  Search,
  Trash2,
  CheckCircle2,
  XCircle,
  Loader2,
  FileSpreadsheet,
  Download,
  RefreshCw,
  Building,
} from 'lucide-react';
import { apiClient } from '../services/api-client';
import { useAuth } from '../context/AuthContext';

interface ContactItem {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  company: string | null;
  tags: string[];
  status: string;
  createdAt: string;
}

export const ContactsPage: React.FC = () => {
  const { currentOrg } = useAuth();
  const [contacts, setContacts] = useState<ContactItem[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [stats, setStats] = useState({ total: 0, subscribed: 0, unsubscribed: 0, bounced: 0 });
  const [loading, setLoading] = useState<boolean>(true);
  const [search, setSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Single Add Modal State
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [adding, setAdding] = useState<boolean>(false);
  const [formEmail, setFormEmail] = useState<string>('');
  const [formFirstName, setFormFirstName] = useState<string>('');
  const [formLastName, setFormLastName] = useState<string>('');
  const [formCompany, setFormCompany] = useState<string>('');
  const [formTags, setFormTags] = useState<string>('');

  // Bulk CSV Upload Modal State
  const [showCsvModal, setShowCsvModal] = useState<boolean>(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvPreview, setCsvPreview] = useState<any[]>([]);
  const [uploading, setUploading] = useState<boolean>(false);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const [listRes, statsRes] = await Promise.all([
        apiClient.getContacts({ search, status: statusFilter, limit: 100 }),
        apiClient.getContactStats(),
      ]);
      setContacts(listRes.contacts || []);
      setTotalCount(listRes.total || 0);
      setStats(statsRes);
    } catch (err: any) {
      console.error('Failed to load contacts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, [currentOrg?.id, statusFilter]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchContacts();
  };

  const handleSingleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setAdding(true);
    setMessage(null);
    try {
      const tagsArray = formTags.split(',').map((t) => t.trim()).filter(Boolean);
      await apiClient.createContact({
        email: formEmail,
        firstName: formFirstName,
        lastName: formLastName,
        company: formCompany,
        tags: tagsArray,
      });
      setMessage({ type: 'success', text: `Contact ${formEmail} added successfully!` });
      setShowAddModal(false);
      setFormEmail('');
      setFormFirstName('');
      setFormLastName('');
      setFormCompany('');
      setFormTags('');
      await fetchContacts();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to add contact' });
    } finally {
      setAdding(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFile(file);

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const lines = text.split('\n').filter((l) => l.trim().length > 0);
      if (lines.length <= 1) return;

      const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
      const parsed = lines.slice(1, 6).map((line) => {
        const values = line.split(',').map((v) => v.trim());
        const obj: any = {};
        headers.forEach((h, idx) => {
          obj[h] = values[idx] || '';
        });
        return obj;
      });
      setCsvPreview(parsed);
    };
    reader.readAsText(file);
  };

  const handleBulkUpload = async () => {
    if (!csvFile) return;
    setUploading(true);
    setMessage(null);
    try {
      const text = await csvFile.text();
      const lines = text.split('\n').filter((l) => l.trim().length > 0);
      if (lines.length <= 1) throw new Error('CSV is empty or lacks headers');

      const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
      const emailIdx = headers.findIndex((h) => h.includes('email'));
      const fnIdx = headers.findIndex((h) => h.includes('first') || h === 'name');
      const lnIdx = headers.findIndex((h) => h.includes('last'));
      const compIdx = headers.findIndex((h) => h.includes('company') || h.includes('org'));

      if (emailIdx === -1) throw new Error('CSV must contain an "email" column');

      const payload = lines.slice(1).map((line) => {
        const parts = line.split(',').map((p) => p.trim());
        return {
          email: parts[emailIdx] || '',
          firstName: fnIdx !== -1 ? parts[fnIdx] : undefined,
          lastName: lnIdx !== -1 ? parts[lnIdx] : undefined,
          company: compIdx !== -1 ? parts[compIdx] : undefined,
          tags: ['CSV Import'],
        };
      }).filter((c) => c.email.includes('@'));

      const res = await apiClient.bulkCreateContacts(payload);
      setMessage({ type: 'success', text: `Successfully imported ${res.inserted} contacts into audience list!` });
      setShowCsvModal(false);
      setCsvFile(null);
      setCsvPreview([]);
      await fetchContacts();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'CSV Import failed' });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: string, email: string) => {
    if (!window.confirm(`Delete contact ${email}?`)) return;
    try {
      await apiClient.deleteContact(id);
      setMessage({ type: 'success', text: `Contact ${email} removed` });
      await fetchContacts();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to delete contact' });
    }
  };

  const downloadSampleCsv = () => {
    const csvContent = 'email,first_name,last_name,company\nalex@acme.test,Alex,Morgan,Acme Corp\nsarah@techflow.test,Sarah,Connor,TechFlow Inc\n';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'antigravity_contacts_sample.csv';
    a.click();
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-800">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2.5">
            <Users className="w-6 h-6 text-brand-400" />
            <span>Audience & Contacts Directory</span>
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Manage target subscriber lists, import contacts via CSV with auto-tagging, and track deliverability subscription statuses.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchContacts}
            disabled={loading}
            className="p-2.5 rounded-xl border border-slate-700 bg-slate-900/80 hover:bg-slate-800 text-slate-300 transition"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setShowCsvModal(true)}
            className="px-3.5 py-2.5 rounded-xl text-xs font-semibold bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 transition flex items-center gap-2"
          >
            <Upload className="w-4 h-4 text-brand-400" />
            Bulk CSV Upload
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2.5 rounded-xl text-xs font-semibold bg-brand-600 hover:bg-brand-500 text-white shadow-lg shadow-brand-950/40 transition flex items-center gap-2"
          >
            <UserPlus className="w-4 h-4" />
            Add Contact
          </button>
        </div>
      </div>

      {/* Status Message */}
      {message && (
        <div
          className={`p-3.5 rounded-xl text-xs flex items-center justify-between border ${
            message.type === 'success'
              ? 'bg-emerald-950/40 border-emerald-800/80 text-emerald-300'
              : 'bg-rose-950/40 border-rose-800/80 text-rose-300'
          }`}
        >
          <div className="flex items-center gap-2">
            {message.type === 'success' ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <XCircle className="w-4 h-4 text-rose-400" />}
            <span>{message.text}</span>
          </div>
          <button onClick={() => setMessage(null)} className="text-slate-400 hover:text-white">
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="p-4 rounded-2xl bg-slate-900/40 border border-slate-800/80 shadow-md">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-400">Total Audience</span>
            <Users className="w-4 h-4 text-brand-400" />
          </div>
          <div className="text-2xl font-bold text-white mt-2">{stats.total}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Subscribers in database</div>
        </div>

        <div className="p-4 rounded-2xl bg-slate-900/40 border border-slate-800/80 shadow-md">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-400">Subscribed (Active)</span>
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
          </div>
          <div className="text-2xl font-bold text-emerald-400 mt-2">{stats.subscribed}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Ready for campaign delivery</div>
        </div>

        <div className="p-4 rounded-2xl bg-slate-900/40 border border-slate-800/80 shadow-md">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-400">Unsubscribed</span>
            <XCircle className="w-4 h-4 text-amber-400" />
          </div>
          <div className="text-2xl font-bold text-amber-400 mt-2">{stats.unsubscribed}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Suppressed from outreach</div>
        </div>

        <div className="p-4 rounded-2xl bg-slate-900/40 border border-slate-800/80 shadow-md">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-400">Bounced / Inactive</span>
            <Trash2 className="w-4 h-4 text-rose-400" />
          </div>
          <div className="text-2xl font-bold text-rose-400 mt-2">{stats.bounced}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Hard bounced / rejected</div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <form onSubmit={handleSearchSubmit} className="relative flex-1 max-w-md w-full">
          <Search className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search contacts by email, name, or company..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-xl bg-slate-900/60 border border-slate-800 text-xs text-white focus:outline-none focus:border-brand-500 placeholder-slate-500"
          />
        </form>

        <div className="flex items-center gap-2">
          {['', 'SUBSCRIBED', 'UNSUBSCRIBED', 'BOUNCED'].map((st) => (
            <button
              key={st}
              onClick={() => setStatusFilter(st)}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition border ${
                statusFilter === st
                  ? 'bg-brand-600 text-white border-brand-500 shadow-md shadow-brand-500/20'
                  : 'bg-slate-900 border-slate-800 text-slate-400 hover:text-white'
              }`}
            >
              {st === '' ? 'All Statuses' : st}
            </button>
          ))}
        </div>
      </div>

      {/* Contacts Table */}
      <div className="bg-slate-900/40 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
        <div className="p-4 bg-slate-900/80 border-b border-slate-800 flex items-center justify-between">
          <h2 className="text-sm font-bold text-white flex items-center gap-2">
            <span>Subscriber Directory</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-slate-800 text-slate-300 font-mono">
              {totalCount} Total
            </span>
          </h2>
          <span className="text-xs text-slate-400">
            Contacts in this list are automatically eligible for dynamic campaigns.
          </span>
        </div>

        {loading ? (
          <div className="p-16 text-center text-slate-400 flex flex-col items-center justify-center gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-brand-400" />
            <span className="text-xs">Loading audience contacts...</span>
          </div>
        ) : contacts.length === 0 ? (
          <div className="p-16 text-center text-slate-400 space-y-3">
            <Users className="w-10 h-10 text-slate-600 mx-auto" />
            <h3 className="text-sm font-bold text-white">No Contacts In List</h3>
            <p className="text-xs text-slate-500 max-w-md mx-auto">
              Add individual recipients or perform a bulk CSV upload to build your recipient database.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-900/60 text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
                  <th className="py-3 px-4">Contact</th>
                  <th className="py-3 px-4">Company</th>
                  <th className="py-3 px-4">Tags</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Date Added</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60">
                {contacts.map((contact) => (
                  <tr key={contact.id} className="hover:bg-slate-800/30 transition-colors">
                    <td className="py-3 px-4">
                      <div className="font-semibold text-white">{contact.email}</div>
                      {(contact.firstName || contact.lastName) && (
                        <div className="text-[11px] text-slate-400">
                          {contact.firstName} {contact.lastName}
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4 text-slate-300">
                      {contact.company ? (
                        <span className="flex items-center gap-1.5">
                          <Building className="w-3.5 h-3.5 text-slate-500" />
                          <span>{contact.company}</span>
                        </span>
                      ) : (
                        <span className="text-slate-600">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-wrap gap-1">
                        {(contact.tags || []).map((t, idx) => (
                          <span
                            key={idx}
                            className="text-[10px] px-2 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-700"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          contact.status === 'SUBSCRIBED'
                            ? 'bg-emerald-950/80 text-emerald-400 border border-emerald-800/80'
                            : contact.status === 'BOUNCED'
                            ? 'bg-rose-950/80 text-rose-400 border border-rose-800/80'
                            : 'bg-amber-950/80 text-amber-400 border border-amber-800/80'
                        }`}
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-current" />
                        {contact.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-slate-500 font-mono text-[11px]">
                      {new Date(contact.createdAt).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <button
                        onClick={() => handleDelete(contact.id, contact.email)}
                        className="p-1.5 text-slate-400 hover:text-rose-400 hover:bg-rose-950/40 rounded-lg transition"
                        title="Delete Contact"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal 1: Single Contact Add */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-md p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-800">
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-brand-400" />
                Add New Contact
              </h3>
              <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-white">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSingleAdd} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">Email Address *</label>
                <input
                  type="email"
                  required
                  placeholder="subscriber@example.com"
                  value={formEmail}
                  onChange={(e) => setFormEmail(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white focus:outline-none focus:border-brand-500 font-mono"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">First Name</label>
                  <input
                    type="text"
                    placeholder="Alex"
                    value={formFirstName}
                    onChange={(e) => setFormFirstName(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white focus:outline-none focus:border-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Last Name</label>
                  <input
                    type="text"
                    placeholder="Morgan"
                    value={formLastName}
                    onChange={(e) => setFormLastName(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white focus:outline-none focus:border-brand-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">Company / Organization</label>
                <input
                  type="text"
                  placeholder="Acme Corp"
                  value={formCompany}
                  onChange={(e) => setFormCompany(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white focus:outline-none focus:border-brand-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">Tags (Comma-separated)</label>
                <input
                  type="text"
                  placeholder="VIP, Early Access, Prospect"
                  value={formTags}
                  onChange={(e) => setFormTags(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white focus:outline-none focus:border-brand-500"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 rounded-xl text-xs font-medium bg-slate-800 text-slate-300 hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={adding}
                  className="px-5 py-2 rounded-xl text-xs font-semibold bg-brand-600 hover:bg-brand-500 text-white flex items-center gap-2"
                >
                  {adding && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Save Contact
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal 2: Bulk CSV Upload */}
      {showCsvModal && (
        <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-xl p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-800">
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <Upload className="w-5 h-5 text-brand-400" />
                Bulk CSV Contact Upload
              </h3>
              <button onClick={() => setShowCsvModal(false)} className="text-slate-400 hover:text-white">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between text-xs text-slate-400">
                <span>Upload a CSV with columns: <strong>email</strong>, <strong>first_name</strong>, <strong>last_name</strong>, <strong>company</strong></span>
                <button
                  onClick={downloadSampleCsv}
                  className="text-brand-400 hover:underline flex items-center gap-1 font-semibold"
                >
                  <Download className="w-3.5 h-3.5" /> Sample CSV
                </button>
              </div>

              <div className="border-2 border-dashed border-slate-700 hover:border-brand-500 rounded-2xl p-6 text-center transition bg-slate-950/40">
                <FileSpreadsheet className="w-10 h-10 text-brand-400 mx-auto mb-2" />
                <p className="text-xs text-white font-semibold">Select your CSV File</p>
                <p className="text-[11px] text-slate-500 mt-0.5">Supports thousands of contacts with auto deduplication</p>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="mt-3 text-xs text-slate-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-brand-600 file:text-white hover:file:bg-brand-500"
                />
              </div>

              {csvPreview.length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs font-semibold text-slate-300">Data Preview (First 5 Rows):</div>
                  <div className="p-3 bg-slate-950 rounded-xl border border-slate-800 max-h-36 overflow-y-auto font-mono text-[11px] text-slate-300">
                    {csvPreview.map((row, idx) => (
                      <div key={idx} className="border-b border-slate-800/60 py-1 last:border-0 truncate">
                        {row.email} | {row.first_name || ''} {row.last_name || ''} ({row.company || 'N/A'})
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowCsvModal(false)}
                  className="px-4 py-2 rounded-xl text-xs font-medium bg-slate-800 text-slate-300 hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleBulkUpload}
                  disabled={!csvFile || uploading}
                  className="px-5 py-2 rounded-xl text-xs font-semibold bg-brand-600 hover:bg-brand-500 text-white flex items-center gap-2 disabled:opacity-50"
                >
                  {uploading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Import All Contacts
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
